import React, {
  useEffect, useState, useCallback, useMemo,
} from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import {
  Theme, Spin, notification,
} from '@routific/hive';
import { useTranslation } from 'react-i18next';
import {
  DragDropContext, DragStart, DragUpdate, DropResult,
} from 'react-beautiful-dnd';
import { useQuery, useStore } from '../../../../store/mst';
import RouteBreakdownQuery from '../../../../store/mst/queries/routes/RouteBreakdownQuery';
import RouteTimeline from './RouteTimeline';
import SkeletonTimeline from './RouteTimeline/SkeletonTimeline';
import SkeletonOverlay from './SkeletonOverlay';
import {
  RouteDtoModelType, RouteStopScheduledDtoModelType, RouteTimelineDtoModelType,
} from '../../../../store/mst/models';
import RouteCard from '../../RouteCard';
import { PANEL_BORDER_COLOR } from '../../../../constants';
import EmptyTimelineCard from './RouteTimeline/TimelineCard/EmptyTimelineCard';
import { rearrangeStopFailedNotification } from '../../../../notifications/scheduling/rearrangeStop';
import { DropzonePosition } from './RouteTimeline/TimelineCard/TimelineCardDropZone';

interface Props {
  route: RouteDtoModelType;
  routeDetailsRef: React.MutableRefObject<HTMLDivElement | null>;
  selectable?: boolean;
  hasMountedPanelMode?: boolean;
  onLoading?: (state: boolean) => void,
}

const CardsContainer = styled.div`
  background: ${Theme.Colors.gray0};
  flex: 1;
  height: 100%;
  overflow: hidden;
  position: relative;
  .ant-spin-nested-loading, .ant-spin-container { height: 100%; }
  .scrollable-body { margin-top: 0; }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${PANEL_BORDER_COLOR};
  margin:0 auto;
`;

const RouteBreakdown: React.FC<Props> = ({
  route, routeDetailsRef, selectable, hasMountedPanelMode, onLoading,
}) => {
  const store = useStore();

  const { t } = useTranslation(['notifications', 'common', 'components']);

  const handleFailRearrangeVisit = (event: RouteTimelineDtoModelType, routeName: string | null | undefined, error?: string) => {
    notification(rearrangeStopFailedNotification(
      store,
      event.eventUuid!,
      event.id || t('unknownRouteId.message'),
      event.stopSequence,
      routeName,
      error,
    ));
  };

  const handleRearrangeVisit = async (event: RouteTimelineDtoModelType, targetStopIndex: number) => {
    if (event.route) {
      try {
        await store.model.rearrangeStopWithinRoute(event.route.uuid, event.eventUuid, targetStopIndex);
      } catch (err) {
        handleFailRearrangeVisit(event, event.route.name, err?.response?.errors?.[0]?.message);
      }
    } else {
      handleFailRearrangeVisit(event, undefined);
    }
  };

  const [dragOriginIndex, setDragOriginIndex] = useState<number | undefined>(undefined);
  const [draggedOverIndex, setDraggedOverIndex] = useState<number | undefined>(undefined);
  const [dropzonePosition, setDropzonePosition] = useState<DropzonePosition | undefined>(undefined);

  const onDragStart = (start?: DragStart) => {
    setDragOriginIndex(start?.source?.index);
  };

  // We cannot use onDragUpdate on its own since it only updates upon hover of a new card.
  // We want to check if the user hovers on the top or bottom of the card, so we extend the logic in TimelineCard
  const onDragUpdate = (update?: DragUpdate) => {
    if (update?.destination?.index === undefined) {
      setDraggedOverIndex(undefined);
      setDropzonePosition(undefined);
    }
  };

  const onDragEnd = useCallback((result: DropResult) => {
    const { source } = result;

    // Abort if drop target is empty
    if (draggedOverIndex === undefined) {
      return;
    }

    const originIndex = source.index;

    let targetIndex = draggedOverIndex;

    // If the dropzone is at the top of the element and the original index is less than the target index, adjust the target index
    // (but ensure it's not less than zero)
    if (dropzonePosition === DropzonePosition.TOP && originIndex < draggedOverIndex) {
      targetIndex = Math.max(0, draggedOverIndex - 1);
    } else if (dropzonePosition === DropzonePosition.BOTTOM && originIndex > draggedOverIndex) {
      // If the dropzone is at the top of the element and the original index is greater than the target index, increment the target index
      targetIndex = draggedOverIndex + 1;
    }

    const event = route.timeline[originIndex];
    const { eventUuid, stopSequence: originStopSequence } = event;
    const { stopSequence: targetStopSequence } = route.timeline[targetIndex];

    // Abort if dropped in the same position
    if (targetStopSequence === originStopSequence) {
      return;
    }

    if (event && eventUuid && targetStopSequence) {
      handleRearrangeVisit(event, targetStopSequence);
    }
  }, [draggedOverIndex, dropzonePosition]);

  const { setQuery, loading } = useQuery();

  // Show skeleton only when timeline data is not yet populated. We intentionally do not
  // use `loading` here so that switching to a route that already has timeline in MST
  // shows content immediately instead of flashing the skeleton during refetch.
  const showSkeleton = !route.timeline?.length;
  const hasRouteBreakdownData = useMemo(() => {
    const hasStopEvents = !!route.timeline?.some((event) => event.eventType === 'delivering_stop');

    if (!hasStopEvents) return true;

    return !!route.timeline?.some((event) => event.eventType === 'delivering_stop'
      && event.orders?.some((order) => order?.hasLoaded?.('name') || order?.hasLoaded?.('locations')));
  }, [route.timeline]);
  const showBreakdownLoading = loading && !hasRouteBreakdownData;

  const queryRouteBreakDown = (routeId: string) => {
    const projectId = store.ui.selectedWorkspace?.id;

    if (routeId) {
      setQuery(store.model.queryRouteById(
        { projectId: String(projectId), routeId },
        RouteBreakdownQuery,
        { fetchPolicy: 'cache-and-network' },
      ));
    }
  };

  useEffect(() => {
    if (onLoading) {
      onLoading(showBreakdownLoading);
    }
  }, [showBreakdownLoading, onLoading]);

  // query route on mount, re-query on stops scheduled to route
  useEffect(() => {
    if (hasMountedPanelMode) {
      queryRouteBreakDown(route.id);
      store.subscription.onRouteEvent('RouteStopScheduledDto', (payload: RouteStopScheduledDtoModelType) => {
        if (route.id === payload.route?.id) {
          queryRouteBreakDown(route.id);
        }
      });
    }
  }, [route, hasMountedPanelMode]);

  const getSpinnerText = () => {
    if (route.isLocked) {
      return t('common:loading.reoptimizing');
    }
    if (route.isReversing) {
      return t('common:loading.reversing');
    }
    return '';
  };

  if ((route?.totalStopsCount || 0) < 1) {
    return (
      <>
        <RouteCard route={route} stateless routeDetailsRef={routeDetailsRef} selectable={selectable} withTitle={false} withColorBar={false} />
        <Divider />
        <CardsContainer>
          <Spin spinning={route.isLocked || !!route.isReversing} tip={getSpinnerText()}>
            <EmptyTimelineCard />
          </Spin>
        </CardsContainer>
      </>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart} onDragUpdate={onDragUpdate}>
      <RouteCard route={route} stateless routeDetailsRef={routeDetailsRef} selectable={selectable} withTitle={false} withColorBar={false} />
      <Divider />
      {!hasMountedPanelMode ? (
        <SkeletonTimeline />
      ) : (
        // CardsContainer and RouteTimeline stay mounted so Virtuoso pre-renders
        // items in the background. The skeleton overlays on top until ready,
        // then disappears - no white flash from an empty Virtuoso mount.
        <CardsContainer>
          <Spin spinning={(route.isLocked || !!route.isReversing)} tip={getSpinnerText()}>
            <RouteTimeline
              route={route}
              isBreakdownLoading={showBreakdownLoading}
              dragDropProps={{
                dragOriginIndex, draggedOverIndex, setDragOriginIndex, setDraggedOverIndex, dropzonePosition, setDropzonePosition,
              }}
            />
          </Spin>
          <SkeletonOverlay show={showSkeleton} />
        </CardsContainer>
      )}
    </DragDropContext>
  );
};

export default observer(RouteBreakdown);
